import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { type CountryCode, isValidPhoneNumber } from 'libphonenumber-js';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { usePhoneConsent } from './use-phone-consent';
import { persistPhoneLastUpdatedUtc, updateSellerPhone } from '../../pages/settings/sign-in-and-security/sections/seller-communication/api';
import { updateSellerPhone as updateSellerPhoneAction } from '../../store/seller/seller.actions';
import { US } from '../../constants/country-codes';

type UseSavePhoneNumberOptions = {
  onSuccess: () => void;
  onError: (error: unknown) => void;
};

type SavePhoneNumberParams = {
  phoneNumber: string;
  isConsentChecked: boolean;
  countryCode?: CountryCode;
};

export const useSavePhoneNumber = ({ onSuccess, onError }: UseSavePhoneNumberOptions) => {
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useAsyncSafeState(false);
  const { saveConsent } = usePhoneConsent();

  const savePhoneNumber = useCallback(
    async ({ phoneNumber, isConsentChecked, countryCode }: SavePhoneNumberParams) => {
      if (!phoneNumber || !isValidPhoneNumber(phoneNumber, countryCode ?? US)) {
        return;
      }

      setIsSaving(true);

      try {
        await updateSellerPhone(phoneNumber);

        if (isConsentChecked) {
          await saveConsent(phoneNumber, true);
        }

        await persistPhoneLastUpdatedUtc();

        // Update Redux state after successful save so UI re-renders
        dispatch(updateSellerPhoneAction(phoneNumber));

        onSuccess();
      } catch (error) {
        onError(error);
      } finally {
        setIsSaving(false);
      }
    },
    [dispatch, onError, onSuccess, saveConsent, setIsSaving],
  );

  return { savePhoneNumber, isSaving };
};
